import React, { useCallback, useEffect, useState } from 'react';
import api from '../wrapper/API';
import '../styles/MainContainer.css';
import './MailQueue.css';
import ActionButton from '../input/ActionButton';
import Popup from '../popup/Popup';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import OfflineBanner from '../shell/OfflineBanner';

const KIND_LABELS = {
  RegistrationConfirmation: 'Eingangsbestätigung',
  ParticipationStatus: 'Teilnahme-Status',
  SpotAvailable: 'Platz frei',
  Cancellation: 'Abmeldung',
  RoundMail: 'Rundmail',
  FreeMail: 'Freimail',
  CustomMail: 'Custom-Mail',
  EventReminder: 'Event-Erinnerung',
  AdminNotification: 'Admin-Benachrichtigung',
  PasswordReset: 'Passwort-Reset',
  Newsletter: 'Newsletter',
  PaymentConfirmation: 'Zahlungsbestätigung',
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function MailQueue() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const { openPopup, closePopup, popupData } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/DeadLetterMail?skip=0&take=100');
      setItems(res.data?.items ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleRetry = async (id) => {
    setBusyId(id);
    startSuccessBarLoading('Mail wird erneut in die Versand-Queue gestellt');
    try {
      await api.post(`/DeadLetterMail/Retry/${id}`);
      startSuccessBarSuccess('Mail erneut gestartet');
      setItems(prev => prev.filter(i => i.id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch {
      closeSuccessBar();
    } finally {
      setBusyId(null);
    }
  };

  const askDelete = (id) => openPopup('confirmDeleteDlqMail', id);
  const handleConfirmDelete = async () => {
    const id = popupData?.identifier;
    closePopup();
    if (!id) return;
    setBusyId(id);
    startSuccessBarLoading('Eintrag wird verworfen');
    try {
      await api.delete(`/DeadLetterMail/${id}`);
      startSuccessBarSuccess('Eintrag verworfen');
      setItems(prev => prev.filter(i => i.id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch {
      closeSuccessBar();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <React.Fragment>
      <main className="defaultMain">
        <OfflineBanner />
        <div className="mailQueueHeader">
          <h2 className="mailQueueTitle">Mailing-Fehler</h2>
          <span className="mailQueueSubtitle">
            {loading ? 'Lade …' : total === 0
              ? 'Keine fehlgeschlagenen Mails'
              : `${total} fehlgeschlagene ${total === 1 ? 'Mail' : 'Mails'}`}
          </span>
        </div>

        {!loading && items.length === 0 && (
          <div className="mailQueueEmpty">
            <span className="material-symbols-rounded mailQueueEmptyIcon">check_circle</span>
            <p>Alle Mails wurden erfolgreich versendet.</p>
          </div>
        )}

        {items.length > 0 && (
          <div className="mailQueueList">
            {items.map(item => (
              <div key={item.id} className="mailQueueItem">
                <div className="mailQueueItemHead">
                  <span className="mailQueueItemKind">{KIND_LABELS[item.kind] ?? item.kind}</span>
                  <span className="mailQueueItemDate">{formatDate(item.createdAt)}</span>
                </div>
                <div className="mailQueueItemRow">
                  <span className="mailQueueLabel">An:</span>
                  <span className="mailQueueValue">{item.toAddress}</span>
                </div>
                <div className="mailQueueItemRow">
                  <span className="mailQueueLabel">Betreff:</span>
                  <span className="mailQueueValue">{item.subject}</span>
                </div>
                <div className="mailQueueItemRow">
                  <span className="mailQueueLabel">Versuche:</span>
                  <span className="mailQueueValue">{item.retryCount}</span>
                </div>
                <div className="mailQueueItemError" title={item.lastError}>
                  <span className="material-symbols-rounded mailQueueErrorIcon">error</span>
                  <span>{item.lastError}</span>
                </div>
                <div className="mailQueueItemActions">
                  <ActionButton
                    icon="refresh"
                    label="Erneut versuchen"
                    type={3}
                    onClick={() => handleRetry(item.id)}
                    disabled={busyId === item.id}
                  />
                  <ActionButton
                    icon="delete"
                    label="Verwerfen"
                    type={1}
                    onClick={() => askDelete(item.id)}
                    disabled={busyId === item.id}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Popup
        name="confirmDeleteDlqMail"
        confirmText="Verwerfen"
        headerText="Eintrag verwerfen"
        type="confirmaction"
        onConfirm={handleConfirmDelete}
        withCancel
      >
        <p>Möchtest du diese fehlgeschlagene Mail wirklich verwerfen? Sie wird nicht mehr versendet.</p>
      </Popup>
    </React.Fragment>
  );
}

export default MailQueue;
